/**
 * Regulatory Checkup — /checkup
 * Public page (no auth). 3-screen flow: Project → Status → Results.
 * Collects the user's email (gate before the full report) plus the project
 * profile they enter (country, capacity, intent, land/grid/offtake answers) —
 * both recorded as a lead (recordWaitlist) and, if they email-gate, attached to
 * the checkup completion. That IS personal data; see the Privacy Policy. The
 * email is used only to send/save the report and follow up about Enereg.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { partitionCheckupLicenses, type CheckupLicenseRow } from "../lib/checkup-view.ts";
import { ChevronRight, ChevronLeft, FileText, AlertTriangle, CheckCircle, ArrowRight, Shield, TrendingUp, Landmark, Leaf, Zap, Mail, Lock } from "lucide-react";
import { getCheckup, attachCheckupEmail, recordWaitlist } from "../lib/api.ts";
import { COUNTRY_META, SHOW_BANKABILITY } from "../lib/constants.ts";
import { status, band } from "../lib/semantics.ts";
import { getIntentOptions, getCapacityHint, getCapacityStep, LOCATION_PLACEHOLDER, EIA_TYPE_OPTIONS, LAND_TENURE_OPTIONS, OFFTAKER_OPTIONS, getCountryQuestions } from "../lib/checkup-options.ts";
import { CHECKUP_QUESTIONS } from "../lib/checkup-questions.ts";
import { formatCapacity } from "../lib/utils.ts";
import type { EvaluationOutput, Nudge } from "../lib/evaluation.ts";
import { ALL_COUNTRIES, isLive, COMING_SOON_BLURB, DEFAULT_CHECKUP_COUNTRY, isValidEmail, REQUEST_EMAIL_MAX, REQUEST_NOTE_MAX, type CheckupCountry } from "../lib/checkup-coverage.ts";

// The six countries this page knows about — derived from checkup-coverage.ts's list, NOT
// re-declared here (the canonical types/field.ts CountryCode spans 22 expansion markets that
// COUNTRY_META and the checkup options don't carry).
type CountryCode = CheckupCountry;

// Which countries the checkup may actually answer for, and which are honestly marked
// not-yet-built. Nigeria is the only one that has been through the regulatory pipeline; the
// checkup used to answer with equal confidence for all six. See lib/checkup-coverage.ts.
const PICKER_COUNTRIES: CountryCode[] = [...ALL_COUNTRIES];

/** Detect embed mode from URL param: /checkup?embed=true */
function useEmbedMode() {
  const params = new URLSearchParams(window.location.search);
  const isEmbed = params.get("embed") === "true";

  const postToParent = useCallback((type: string, data?: Record<string, any>) => {
    if (isEmbed && window.parent !== window) {
      // Restrict to enereg.com domains; falls back to "*" only if referrer is unavailable
      const origin = document.referrer ? new URL(document.referrer).origin : "*";
      window.parent.postMessage({ type, ...data }, origin);
    }
  }, [isEmbed]);

  // Send height to parent — debounced, avoids infinite mutation loop
  useEffect(() => {
    if (!isEmbed) return;
    let rafId = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sendHeight);
    });

    function sendHeight() {
      observer.disconnect();
      document.body.style.height = 'auto';
      document.body.style.overflow = 'hidden';
      const height = document.body.scrollHeight;
      document.body.style.height = '';
      document.body.style.overflow = '';
      postToParent("enereg-checkup-resize", { height });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    sendHeight();
    window.addEventListener("resize", sendHeight);
    return () => { cancelAnimationFrame(rafId); observer.disconnect(); window.removeEventListener("resize", sendHeight); };
  }, [isEmbed, postToParent]);

  return { isEmbed, postToParent };
}

interface CheckupState {
  country: CountryCode;
  capacity_kw: number;
  intent: string;
  fields: Record<string, string>;
}

// Map generalized answers to field values the evaluation engine understands
function mapAnswersToFields(state: CheckupState): Record<string, string> {
  const mapped: Record<string, string> = {};

  for (const [id, val] of Object.entries(state.fields)) {
    if (!val || val === "" || val === "Not yet secured" || val === "Not yet determined" || val === "Not started" || val === "Not yet") continue;

    switch (id) {
      case "S-009":
        if (val === "Yes") mapped["S-009"] = "Secured";
        break;
      case "E-005":
        if (val === "Yes, approved") mapped["E-005"] = "Approved";
        break;
      case "G-003":
        if (val === "Yes") mapped["G-003"] = "Connected";
        else if (val === "Applied") mapped["G-003"] = "Applied";
        break;
      case "F-001":
        if (val === "Under $500K") mapped["F-001"] = "250000";
        else if (val === "$500K - $2M") mapped["F-001"] = "1250000";
        else if (val === "$2M - $10M") mapped["F-001"] = "6000000";
        else if (val === "$10M - $50M") mapped["F-001"] = "30000000";
        else if (val === "Over $50M") mapped["F-001"] = "75000000";
        break;
      case "F-006":
        if (val === "Yes, agreed with offtaker") mapped["F-006"] = "Agreed";
        else if (val === "Estimated but not agreed") mapped["F-006"] = "Estimated";
        break;
      case "F-011":
        if (val !== "Not yet determined") mapped["F-011"] = val;
        break;
      default:
        mapped[id] = val;
        break;
    }
  }

  if (state.fields["P-002"]) mapped["P-002"] = state.fields["P-002"];
  mapped["C-001"] = "Company registered";
  mapped["C-002"] = "Registered";

  return mapped;
}

// ─── Print styles ───
const PRINT_STYLES = `
@media print {
  body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .print-hide { display: none !important; }
  .print-show { display: block !important; }
  /* A printed page must not inherit dark-mode surfaces from a user reading in
     dark mode. Re-declare the LIGHT token values on the .dark scope so every
     utility resolves light under print — patching individual classes missed
     text and pill tokens and printed near-black cards with near-white text. */
  .dark {
    --color-surface: #FFFFFF; --color-surface-secondary: #F0F4F8; --color-surface-tertiary: #D9E2EC;
    --color-border: #D9E2EC; --color-border-secondary: #BCCCDC;
    --color-text-primary: #102A43; --color-text-secondary: #486581; --color-hairline: #829AB1; --color-text-tertiary: #486581;
    --color-brand: #0F6E56; --color-brand-light: #E1F5EE; --color-on-solid: #FFFFFF; --color-on-brand: #FFFFFF;
    --color-danger: #A32D2D; --color-danger-light: #FCEBEB; --color-info: #185FA5; --color-info-light: #E6F1FB;
    --color-ai: #534AB7; --color-ai-light: #EEEDFE;
    --color-warning-fill: #BA7517; --color-warning-text: #925C12; --color-warning-light: #FAEEDA;
  }
  section, .bg-surface { break-inside: avoid; box-shadow: none !important; }
  @page { margin: 1cm; size: A4; }
}
`;

const STEP_NAMES: string[] = ["Project", "Status", "Results"];
const SKELETON_CARDS = [1, 2, 3, 4] as const;
const SKELETON_ROWS = [1, 2, 3, 4, 5] as const;

export function Checkup() {
  const { isEmbed, postToParent } = useEmbedMode();
  const [step, setStep] = useState(0); // 0=Project, 1=Status, 2=Results
  // The country a visitor asked us to BUILD, not the one they are checking. Null unless they
  // clicked a coming-soon card.
  const [requestCountry, setRequestCountry] = useState<CountryCode | null>(null);
  const [state, setState] = useState<CheckupState>({
    country: DEFAULT_CHECKUP_COUNTRY,
    capacity_kw: 5000,
    intent: "",
    fields: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EvaluationOutput | null>(null);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [checkupToken, setCheckupToken] = useState<string | null>(null);
  // Notify parent of step changes
  useEffect(() => { postToParent("enereg-checkup-step", { step }); }, [step, postToParent]);

  const stepNames = STEP_NAMES;

  function updateField(fieldId: string, value: string) {
    setState(prev => ({ ...prev, fields: { ...prev.fields, [fieldId]: value } }));
  }

  // The three corpus-driven questions that resolve the "conditional" licences (see
  // lib/checkup-questions.ts). They live BEHIND the email gate: the free roadmap is the teaser,
  // and answering these is what turns four maybes into definite answers — the reward for the
  // email. Kept apart from `state.fields` because they are attributes, not field values, and
  // evaluateCheckup reads them by their own keys.
  const [sharpeners, setSharpeners] = useState<Record<string, string>>({});
  // Sharpen re-runs refine a result that is already on screen; they must not tear it down.
  const [refining, setRefining] = useState(false);
  const [sharpenError, setSharpenError] = useState<string | null>(null);
  // Answers can arrive out of order (each re-run is its own POST); only the newest request may
  // write state, or a slow earlier answer overwrites a later one and the page contradicts the
  // pressed buttons.
  const runSeq = useRef(0);

  async function handleGenerate(extraAttrs: Record<string, string> = {}, refine = false) {
    const seq = ++runSeq.current;
    if (refine) {
      // The result stays mounted — no skeleton, no artificial delay. The evaluation itself is
      // milliseconds of SQLite; the 1800ms "analyzing" theater below is for the FIRST run only.
      setRefining(true);
      setSharpenError(null);
    } else {
      setLoading(true);
      setError(null);
      setStep(2);
    }

    try {
      // The sharpening answers ride in on `fields` — the channel evaluateCheckup reads
      // ATTRIBUTE_KEYS out of. Field ids are X-000-shaped, so these snake_case keys cannot
      // collide with a real field value.
      const mapped = { ...mapAnswersToFields(state), ...extraAttrs };
      const request = getCheckup({
        country_code: state.country,
        capacity_kw: state.capacity_kw,
        fields: mapped,
        intent: state.intent || undefined,
      });
      const [evaluation] = await Promise.all([
        request,
        ...(refine ? [] : [new Promise(r => setTimeout(r, 1800))]),
      ]);
      if (seq !== runSeq.current) return; // a newer answer superseded this run
      setResult(evaluation);
      // The server mints the signup ticket when it runs the evaluation — capture it ONCE.
      // This, not the Supabase waitlist insert below, is the credential a completed checkup
      // presents at signup (see auth.ts checkAdmission). Sharpen re-runs mint fresh tokens,
      // but the email was attached to the first — keep it, or the lead linkage is orphaned.
      if (!refine && (evaluation as any)?.checkup_token) setCheckupToken((evaluation as any).checkup_token);
    } catch (e: any) {
      if (seq !== runSeq.current) return;
      // A failed refinement must not destroy the report already on screen: keep the previous
      // result rendered and say so inline. Only a failed FIRST run gets the full error screen.
      if (refine) setSharpenError(e.message || "That answer didn't save. Try it again.");
      else setError(e.message || "Something went wrong. Please try again.");
    } finally {
      if (seq === runSeq.current) {
        if (refine) setRefining(false);
        else setLoading(false);
      }
    }
  }

  // Results step
  if (step === 2) {
    if (loading) {
      return (
        <div className={`${isEmbed ? "px-2 py-4" : "max-w-3xl mx-auto px-4 py-8"}`}>
          {!isEmbed && <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />}
          {!isEmbed && <CheckupHeader country={state.country} />}
          {/* Skeleton mimics results layout */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {SKELETON_CARDS.map(i => (
              <div key={i} className="bg-surface border border-border rounded-xl p-3">
                <div className="skeleton h-3 w-16 mb-2 mx-auto" />
                <div className="skeleton h-5 w-12 mx-auto" />
              </div>
            ))}
          </div>
          <div className="bg-surface border border-border rounded-2xl p-5 mb-4">
            <div className="skeleton h-4 w-40 mb-4" />
            <div className="space-y-2">
              {SKELETON_ROWS.map(i => (
                <div key={i} className="skeleton h-12 rounded-xl" />
              ))}
            </div>
          </div>
          <p className="text-[14px] text-text-tertiary text-center">Analyzing your project against {COUNTRY_META[state.country].name}'s regulatory requirements...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className={`${isEmbed ? "px-2 py-4" : "max-w-3xl mx-auto px-4 py-8"}`}>
          {!isEmbed && <CheckupHeader country={state.country} />}
          <div className="bg-surface border border-danger/20 rounded-2xl p-6 text-center">
            <AlertTriangle size={24} className="text-danger mx-auto mb-2" />
            <p className="text-[14px] text-danger mb-4">{error}</p>
            <button
              onClick={() => {
                // A full restart. Clear the sharpener answers too — they describe the PREVIOUS
                // project, and silently merging them into a new run (handleGenerate's default)
                // would rule licences out of a project nobody answered for.
                setStep(0); setError(null); setResult(null); setSharpeners({}); setSharpenError(null);
              }}
              className="px-4 py-1.5 bg-brand text-on-solid rounded-xl text-[14px] font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (result) {
      return (
        <CheckupResults
          result={result}
          state={state}
          checkupToken={checkupToken}
          email={email}
          emailSubmitted={emailSubmitted}
          onEmailChange={setEmail}
          onEmailSubmit={() => {
            setEmailSubmitted(true);
            postToParent("enereg-checkup-email", { email });
            // Lead capture (marketing). This is NOT the signup credential — the token comes
            // from the server-side evaluation (setCheckupToken above). Attaching the email to
            // the server completion lets us follow up with non-savers and records who the
            // checkup belonged to; a failure here must not block the save flow.
            if (checkupToken) {
              attachCheckupEmail(checkupToken, email.trim().toLowerCase()).catch(() => {});
            }
            // Via the server (rate-gated, validated) — the SPA no longer writes Supabase
            // directly. Fire-and-forget: lead capture must never block the unlock.
            recordWaitlist({
              source: "checkup",
              email,
              country: state.country,
              capacity: formatCapacity(state.capacity_kw),
              // The pre-fill join key: /api/checkup-snapshot resolves the snapshot ONLY by this
              // token (never by email — see waitlist-server.ts). No token → lead still records,
              // the wizard just starts blank.
              checkup_token: checkupToken ?? undefined,
              checkup_snapshot: {
                country: state.country,
                capacity_kw: state.capacity_kw,
                intent: state.intent,
                fields: state.fields,
                mapped_fields: mapAnswersToFields(state),
              },
            }).catch(() => {});
          }}
          onStartOver={() => { setStep(0); setResult(null); setEmailSubmitted(false); setSharpeners({}); setSharpenError(null); postToParent("enereg-checkup-step", { step: 0 }); }}
          isEmbed={isEmbed}
          postToParent={postToParent}
          sharpeners={sharpeners}
          refining={refining}
          sharpenError={sharpenError}
          onSharpen={(attr, value) => {
            const next = { ...sharpeners, [attr]: value };
            setSharpeners(next);
            handleGenerate(next, true); // refine in place: no skeleton, the counts just move
          }}
        />
      );
    }
  }

  return (
    <div className={`${isEmbed ? "px-2 py-4" : "max-w-xl mx-auto px-4 py-8"}`}>
      {!isEmbed && <CheckupHeader country={state.country} />}

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-8">
        {stepNames.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full text-[13px] font-medium flex items-center justify-center ${
                i < step ? "bg-brand text-on-solid" : i === step ? "bg-brand text-on-solid" : "bg-surface-tertiary text-text-tertiary"
              }`}
            >
              {i < step ? <CheckCircle size={14} /> : i + 1}
            </div>
            <span className={`text-[14px] ${i === step ? "text-text-primary font-medium" : "text-text-tertiary"}`}>
              {s}
            </span>
            {i < stepNames.length - 1 && <ChevronRight size={14} className="text-text-tertiary mx-1" />}
          </div>
        ))}
      </div>

      <div className="bg-surface border border-border rounded-2xl p-5">
        {/* Step 0: Your Project */}
        {step === 0 && (
          <div className="space-y-5">
            <CountrySelector
              countries={PICKER_COUNTRIES}
              selected={state.country}
              onSelect={(code) => {
                if (code !== state.country) {
                  const { "E-001": _, "S-010": __, ...rest } = state.fields;
                  setState({ ...state, country: code, intent: "", fields: rest });
                }
                setRequestCountry(null);
              }}
              onRequest={(code) => setRequestCountry(code)}
            />
            {requestCountry && (
              // key: each country gets a FRESH panel. Without it React reuses the instance, and a
              // "done" confirmation for Kenya survives a click on Ghana — a recorded-looking
              // request that was never recorded.
              <RegulatorySetRequest
                key={requestCountry}
                country={requestCountry}
                onClose={() => setRequestCountry(null)}
              />
            )}
            <div>
              <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                Plant Capacity (AC Export)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={state.capacity_kw || ""}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      setState({ ...state, capacity_kw: 0, intent: "" });
                      return;
                    }
                    const kw = Math.round(Number(e.target.value));
                    if (!Number.isFinite(kw)) return;
                    setState({ ...state, capacity_kw: Math.max(kw, 1), intent: "" });
                  }}
                  placeholder="50"
                  step={getCapacityStep(state.capacity_kw)}
                  min="0"
                  className="w-28 px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
                />
                <span className="text-[15px] text-text-secondary">kW</span>
              </div>
              {(() => {
                const hint = getCapacityHint(state.country, state.capacity_kw);
                return hint ? <p className="text-[12px] text-brand mt-1.5">{hint}</p> : null;
              })()}
            </div>
            <div>
              <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                Intended Use
              </label>
              <div className="space-y-2">
                {getIntentOptions(state.country, state.capacity_kw).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setState({ ...state, intent: opt.value })}
                    className={`w-full text-left px-3 py-2 rounded-xl border transition-colors text-[14px] ${
                      state.intent === opt.value ? "border-brand bg-brand-light font-medium" : "border-border hover:border-border-secondary"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-text-secondary mb-1">Project location</label>
              <input
                type="text"
                value={state.fields["S-003"] || ""}
                onChange={(e) => updateField("S-003", e.target.value)}
                placeholder={LOCATION_PLACEHOLDER[state.country] || "e.g., project location"}
                className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
              />
            </div>
          </div>
        )}

        {/* Step 1: Project Status (all status questions combined) */}
        {step === 1 && (
          <div className="space-y-5">
            <p className="text-[12px] text-text-tertiary">All questions are optional. Answer what you can for a more accurate checkup.</p>
            <div>
              <h3 className="text-[14px] font-medium text-text-primary mb-3">Land</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Land tenure type</label>
                  <select value={state.fields["S-010"] || ""} onChange={(e) => updateField("S-010", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {[...LAND_TENURE_OPTIONS[state.country], "Not yet secured"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Site area (hectares)</label>
                  <input type="number" value={state.fields["S-005"] || ""} onChange={(e) => updateField("S-005", e.target.value)}
                    placeholder="e.g., 2.5" className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30" />
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Land title or lease agreement?</label>
                  <select value={state.fields["S-009"] || ""} onChange={(e) => updateField("S-009", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Yes", "In progress", "No"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              {/* Country-specific land question */}
              {(() => {
                const cq = getCountryQuestions(state.country).find(q => q.section === "land");
                if (!cq) return null;
                return (
                  <div className="mt-3">
                    <label className="block text-[13px] text-text-secondary mb-1">{cq.label}</label>
                    <select value={state.fields[cq.id] || ""} onChange={(e) => updateField(cq.id, e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                      <option value="">— Select —</option>
                      {cq.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                );
              })()}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="text-[14px] font-medium text-text-primary mb-3">Environmental & Grid</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">EIA study type</label>
                  <select value={state.fields["E-001"] || ""} onChange={(e) => updateField("E-001", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {[...EIA_TYPE_OPTIONS[state.country], "Not yet determined"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">EIA license status</label>
                  <select value={state.fields["E-005"] || ""} onChange={(e) => updateField("E-005", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Yes, approved", "Application submitted", "Study in progress", "Not started"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Grid connection agreement?</label>
                  <select value={state.fields["G-003"] || ""} onChange={(e) => updateField("G-003", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Yes", "Applied", "Not yet"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Country-specific env/grid question */}
                {(() => {
                  const cq = getCountryQuestions(state.country).find(q => q.section === "env");
                  if (!cq) return null;
                  return (
                    <div>
                      <label className="block text-[13px] text-text-secondary mb-1">{cq.label}</label>
                      <select value={state.fields[cq.id] || ""} onChange={(e) => updateField(cq.id, e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                        <option value="">— Select —</option>
                        {cq.options.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="text-[14px] font-medium text-text-primary mb-3">Financial</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Estimated project cost (USD)</label>
                  <select value={state.fields["F-001"] || ""} onChange={(e) => updateField("F-001", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Under $500K", "$500K - $2M", "$2M - $10M", "$10M - $50M", "Over $50M"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Expected tariff rate?</label>
                  <select value={state.fields["F-006"] || ""} onChange={(e) => updateField("F-006", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Yes, agreed with offtaker", "Estimated but not agreed", "Not yet determined"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Funding status</label>
                  <select value={state.fields["F-011"] || ""} onChange={(e) => updateField("F-011", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Fully self-funded", "Debt financing secured", "Seeking financing", "Grant-funded", "Not yet determined"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="text-[14px] font-medium text-text-primary mb-3">Offtake</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Who is your intended offtaker?</label>
                  <select value={state.fields["O-001"] || ""} onChange={(e) => updateField("O-001", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {OFFTAKER_OPTIONS[state.country].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Country-specific offtake question (e.g., GH ECG creditworthiness) */}
                {(() => {
                  const cq = getCountryQuestions(state.country).find(q => q.section === "offtake");
                  if (!cq) return null;
                  return (
                    <div>
                      <label className="block text-[13px] text-text-secondary mb-1">{cq.label}</label>
                      <select value={state.fields[cq.id] || ""} onChange={(e) => updateField(cq.id, e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                        <option value="">— Select —</option>
                        {cq.options.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  );
                })()}
              </div>
            </div>

          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-[14px] text-text-secondary hover:text-text-primary">
              <ChevronLeft size={14} /> Back
            </button>
          ) : <div />}

          {step === 0 ? (
            <button
              onClick={() => setStep(1)}
              disabled={!state.capacity_kw}
              className="flex items-center gap-1 px-4 py-1.5 bg-brand text-on-solid rounded-xl text-[16px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={() => {
                // A fresh run answers for THIS project only. Any sharpener answers still hanging
                // around (an error-screen restart, a back-navigation) describe the previous one.
                setSharpeners({});
                setSharpenError(null);
                handleGenerate();
              }}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-brand text-on-solid rounded-xl text-[16px] font-medium"
            >
              <Shield size={14} /> Run Checkup
            </button>
          )}
        </div>
      </div>

      {!isEmbed && (
        <p className="text-[12px] text-text-tertiary text-center mt-4">
          Free regulatory checkup. No account required. No personal data collected.
        </p>
      )}
    </div>
  );
}

// ─── Country Selector with horizontal scroll ───

/**
 * The country picker.
 *
 * Only countries whose regulatory set has actually been researched are selectable. The rest are
 * shown — visibly, deliberately — as not-yet-built, and clicking one opens a request rather than
 * a dead end. See checkup-coverage.ts: the checkup used to answer confidently for all six
 * countries when only Nigeria has been through the regulatory pipeline, and a confident answer
 * drawn from an unverified seed is a bluff at the scale of a whole country.
 *
 * The request is the point, not a consolation prize. It is how we learn which regulatory set to
 * build next — from the people who need it, with their email attached.
 */
function CountrySelector({ countries, selected, onSelect, onRequest }: {
  countries: CountryCode[];
  selected: CountryCode;
  onSelect: (code: CountryCode) => void;
  onRequest: (code: CountryCode) => void;
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
        Country
      </label>

      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide scroll-smooth snap-x snap-mandatory">
        {countries.map((code) => {
          const live = isLive(code);
          return (
            <button
              key={code}
              onClick={() => (live ? onSelect(code) : onRequest(code))}
              aria-label={
                live
                  ? COUNTRY_META[code].name
                  : `${COUNTRY_META[code].name} — coming soon. Request this regulatory set.`
              }
              className={`relative flex-shrink-0 flex flex-col items-center gap-1 w-[130px] p-2.5 rounded-xl border transition-colors snap-start ${
                !live
                  ? "border-dashed border-border bg-surface-secondary hover:border-brand"
                  : selected === code
                    ? "border-brand bg-brand-light"
                    : "border-border hover:border-border-secondary"
              }`}
            >
              <span className={`text-xl ${live ? "" : "opacity-45 grayscale"}`}>
                {COUNTRY_META[code].flag}
              </span>
              <span className={`text-[13px] font-medium ${live ? "" : "text-text-secondary"}`}>
                {COUNTRY_META[code].name}
              </span>
              {!live && (
                <span className="text-[11px] font-medium uppercase tracking-wider text-text-secondary">
                  Coming soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      <p className="mt-2 text-[13px] text-text-secondary">
        Nigeria is live today. The others are not built yet — click one to tell us you need it, and
        we'll use that to decide what we research next.
      </p>
    </div>
  );
}

/**
 * "Request this regulatory set."
 *
 * Writes a demand signal to the same Supabase waitlist the checkup already uses, tagged
 * `source: "regulatory_request"` and carrying the country asked for and — the part worth having —
 * whatever the developer types about the project they are trying to build there. That free text is
 * the research brief, in their words.
 */
function RegulatorySetRequest({ country, onClose }: { country: CountryCode; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");

  const valid = isValidEmail(email);

  async function submit() {
    if (!isValidEmail(email)) return;
    setState("sending");
    // Via the server (rate-gated; the row is shaped and capped in lib/waitlist-server.ts).
    // Never dead-end on our own plumbing: the request is recorded or the developer is told
    // plainly, and either way they get an address that reaches a human.
    const recorded = await recordWaitlist({ source: "regulatory_request", email, country, note })
      .catch(() => false);
    setState(recorded ? "done" : "error");
  }

  if (state === "done") {
    return (
      <div className="mt-4 rounded-xl border border-brand bg-brand-light p-4">
        <p className="text-[15px] font-medium">
          Noted — you want {COUNTRY_META[country].name}.
        </p>
        {/* No "we'll email you" here: there is no mailer in this codebase, and this PR's own
            rule is not to promise what isn't shipped. The recorded request — with the email on
            it — is the promise we can keep. */}
        <p className="mt-1 text-[14px] text-text-secondary">
          Requests are how we choose which regulatory set to build next. Yours is recorded, with
          your email on it, and it counts.
        </p>
        <button className="mt-3 text-[14px] font-medium text-brand underline" onClick={onClose}>
          Back to the checkup
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-border bg-surface p-4">
      <div className="flex items-start gap-2">
        <span className="text-xl opacity-45 grayscale">{COUNTRY_META[country].flag}</span>
        <div className="flex-1">
          <p className="text-[15px] font-medium">
            {COUNTRY_META[country].name} isn't built yet
          </p>
          <p className="mt-1 text-[14px] text-text-secondary">{COMING_SOON_BLURB}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          aria-label="Your email"
          maxLength={REQUEST_EMAIL_MAX}
          className="w-full rounded-xl border border-border px-3 py-2 text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
        />
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder={`What are you trying to build in ${COUNTRY_META[country].name}? (optional — but it tells us what to research first)`}
          aria-label="What are you building there"
          maxLength={REQUEST_NOTE_MAX}
          className="w-full rounded-xl border border-border px-3 py-2 text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
        />

        {state === "error" && (
          <p className="text-[13px] text-danger">
            Couldn't record that. Email{" "}
            <a className="underline" href={`mailto:team@enereg.com?subject=Regulatory set request: ${COUNTRY_META[country].name}`}>
              team@enereg.com
            </a>{" "}
            and we'll log it by hand.
          </p>
        )}

        <div className="flex gap-2">
          <button
            onClick={submit}
            disabled={!valid || state === "sending"}
            className="rounded-xl bg-brand px-4 py-2 text-[15px] font-medium text-on-brand hover:bg-brand-hover disabled:opacity-50"
          >
            {state === "sending" ? "Sending…" : `Request ${COUNTRY_META[country].name}`}
          </button>
          <button onClick={onClose} className="px-3 py-2 text-[15px] text-text-secondary hover:text-text-primary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckupHeader({ country }: { country?: CountryCode }) {
  const countryName = country ? COUNTRY_META[country].name : "your target market";
  return (
    <div className="mb-6">
      <span className="text-[22px] font-bold" style={{ letterSpacing: "-0.02em" }}>Regulatory Checkup</span>
      <p className="text-[14px] text-text-secondary mt-1">
        Find out which licenses and permits your solar project needs in {countryName}, and see where you stand.
      </p>
    </div>
  );
}

// ─── Results ───

const DIMENSION_ICONS: Record<string, any> = {
  "Technical": Zap, "Environmental": Leaf, "Financial": TrendingUp, "Legal / Land": Landmark, "Regulatory": Shield,
};

const CRITICALITY_LABELS: Record<string, string> = {
  critical_path: "Critical path", high: "High priority", medium: "Medium", low: "Low",
};

/**
 * "Sharpen your result" — the substance behind the email gate.
 *
 * Three questions, each read straight off a corpus applicability condition (see
 * lib/checkup-questions.ts). Every one resolves a licence the teaser could only call "conditional".
 * Answering "No" is a first-class outcome: it removes a requirement, and telling a developer they
 * do NOT need SONCAP is worth as much as telling them they do.
 *
 * These are not our opinions about regulation — they are the questions the regulatory corpus says
 * it needs answered. If a conditional licence ever shows up whose deciding fact isn't asked here,
 * checkup-questions.test.ts fails.
 */
function SharpenPanel({ sharpeners, onSharpen, refining, sharpenError }: {
  sharpeners: Record<string, string>;
  onSharpen: (attribute: string, value: string) => void;
  refining: boolean;
  sharpenError: string | null;
}) {
  return (
    <section className="bg-brand-light border border-brand/20 rounded-2xl p-5 mb-4">
      <h2 className="text-[17px] font-medium mb-1">Sharpen your result</h2>
      <p className="text-[14px] text-text-secondary mb-4">
        A few of the requirements above depend on facts only you know. Answer these and we'll rule
        each one in or out — a "no" removes a requirement just as surely as a "yes" adds one. You
        can change an answer any time.
      </p>
      <div className="space-y-3">
        {CHECKUP_QUESTIONS.map((q) => {
          const current = sharpeners[q.attribute];
          const answered = current !== undefined;
          const questionId = `sharpen-${q.attribute}`;
          return (
            <div key={q.attribute} className="bg-surface border border-border rounded-xl p-3.5">
              <div id={questionId} className="text-[15px] font-medium">{q.question}</div>
              <div className="text-[13px] text-text-secondary mt-0.5 mb-2.5">{q.why}</div>
              <div className="flex gap-2" role="group" aria-labelledby={questionId}>
                {([["Yes", q.yes], ["No", q.no]] as const).map(([label, value]) => {
                  const selected = answered && current === value;
                  return (
                    <button
                      key={label}
                      onClick={() => onSharpen(q.attribute, value)}
                      aria-pressed={selected}
                      disabled={refining}
                      className={`px-4 py-1.5 rounded-lg text-[14px] font-medium border transition-colors disabled:opacity-60 ${
                        selected
                          ? "border-brand bg-brand text-on-brand hover:bg-brand-hover"
                          : "border-border hover:border-brand"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {sharpenError && (
        <p className="mt-3 text-[13px] text-danger" role="alert">
          {sharpenError} The requirements above still show your previous answers.
        </p>
      )}
    </section>
  );
}

function CheckupResults({ result, state, email, emailSubmitted, onEmailChange, onEmailSubmit, onStartOver, isEmbed, postToParent, checkupToken, sharpeners, onSharpen, refining, sharpenError }: {
  result: EvaluationOutput; state: CheckupState; email: string; emailSubmitted: boolean;
  onEmailChange: (v: string) => void; onEmailSubmit: () => void; onStartOver: () => void;
  isEmbed?: boolean; postToParent?: (type: string, data?: Record<string, any>) => void;
  checkupToken?: string | null;
  sharpeners: Record<string, string>;
  onSharpen: (attribute: string, value: string) => void;
  refining: boolean;
  sharpenError: string | null;
}) {
  const topNudge = result.nudges[0];
  // Shorten title for summary card
  const topPriorityValue = topNudge
    ? topNudge.title.replace(/\s*—.*/, "").slice(0, 25)
    : "All clear";
  const topPrioritySub = topNudge
    ? `${CRITICALITY_LABELS[topNudge.criticality] || topNudge.criticality} · ~${topNudge.effort}`
    : "No actions needed";

  // Three tiers, and the difference between them is the whole point of the checkup.
  //   required     — binds you, on the answers you gave
  //   conditional  — a QUESTION FOR YOU: it depends on a fact we didn't ask for
  //   under review — a GAP ON OUR SIDE: the corpus records no basis for it yet
  // A row with no tag is treated as under-review, never as required: an untagged row is one
  // we know nothing about, and "we don't know" must never render as "you must have this".
  // Tier on `undetermined` (a FACT about the corpus), not on the status. The status of an
  // undetermined row stays `applicable` on purpose so the project workplan keeps working —
  // see the doctrine note in applicability.ts. The checkup is the surface that must not
  // over-claim, so it is the one that reads the flag.
  //
  // And BEFORE any of that: is this a day-one obligation on this developer at all? The corpus
  // answers that (`surface`), and it is a different question from "does it apply to you".
  // A tariff uplift you may decline, a renewal of a licence you do not hold yet, and a
  // certification that binds your electrical contractor are all real — and none of them
  // belongs in "here is what you must do now". Lumping them in is how the answer reached 35.
  // The partition lives in lib/checkup-view.ts, where it can be TESTED. It used to live here,
  // and that is precisely why the "choose one route" bug — which told a developer they needed
  // only one of two mandatory, prerequisite-linked filings — could be reintroduced during
  // review with the whole suite green. There is no component-test setup in this repo, so a
  // rule that decides whether someone sees a licence they legally need does not get to sit in
  // a render function.
  const {
    required: requiredLics,
    conditional: conditionalLics,
    underReview: underReviewLics,
    elective: electiveLics,
    lifecycle: lifecycleLics,
    thirdParty: thirdPartyLics,
  } = partitionCheckupLicenses(result.per_license_readiness as CheckupLicenseRow[]);

  // `journey_group` is NOT exclusivity. It is a PRESENTATIONAL grouping — the corpus defines it
  // as "rows sharing a group render as one workplan card" (osEnereg promotion.ts), and the
  // generator auto-backfills `spine:<phase>` onto any row that lacks one, purely to hit a lens
  // coverage target. Reading it as "pick one of these" told a grid-connected developer they
  // needed only ONE of {TCN Grid Code Accession, Transmission Use of System} — two mandatory
  // filings where the first is a documented PREREQUISITE of the second. That is under-showing a
  // required licence: the one unrecoverable mistake this product can make.
  //
  // The five NG land-tenure rows genuinely ARE alternative routes, but the corpus does not say
  // so anywhere, and inferring it from a grouping field is guessing. Over-showing five routes is
  // recoverable; telling someone to skip a mandatory agreement is not. Raised with the corpus
  // team instead — exclusivity needs its own field. Until then, every row stands on its own.

  return (
    <div className={`${isEmbed ? "px-2 py-4" : "max-w-3xl mx-auto px-4 py-8"}`}>
      {!isEmbed && <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />}
      {!isEmbed && <CheckupHeader country={state.country} />}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {/* The headline number is what a developer checks first, so it must mean ONE thing.
            It used to be every licence matching capacity — transmission agreements included,
            for an off-grid village. Now it counts only what is actually required on the
            answers given; the conditional and under-review tiers get their own count below. */}
        <SummaryCard label="Licenses Required" value={String(requiredLics.length)} sub={`for ${formatCapacity(state.capacity_kw)} in ${COUNTRY_META[state.country].name}`} />
        <SummaryCard label="Readiness" value={`${result.readiness_pct}%`} sub="based on your answers" />
        {SHOW_BANKABILITY && <SummaryCard label="Bankability" value={result.bankability.grade.replace(/_/g, " ")} sub={`${result.bankability.overall_score}% score`} />}
        <SummaryCard label="Top Priority" value={topPriorityValue} sub={topPrioritySub} />
      </div>

      {/* License roadmap */}
      <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
        <h2 className="text-[17px] font-medium mb-3">Your License Roadmap</h2>
        <p className="text-[13px] text-text-tertiary mb-4">
          {requiredLics.length > 0
            ? <>A {formatCapacity(state.capacity_kw)} solar project in {COUNTRY_META[state.country].name} requires these {requiredLics.length} licenses and permits.</>
            : <>Based on your answers, we can’t yet confirm any licence as unconditionally required for a {formatCapacity(state.capacity_kw)} project in {COUNTRY_META[state.country].name}. The items below are what may apply.</>}
        </p>
        <div className="space-y-2">
          {requiredLics.map((lic) => {
            // Same readiness scale as HealthRing and the dashboard — the days
            // of 65% reading amber here and green there are over. Zero keeps
            // the neutral not-started treatment.
            const licBand = lic.readiness_pct > 0 ? band(lic.readiness_pct, "readiness") : null;
            const accentColor = licBand === "strong" ? "var(--color-brand)"
              : licBand === "adequate" ? "var(--color-info)"
              : licBand ? "var(--color-warning-fill)"
              : "var(--color-border)";
            const fillBg = licBand === "strong" ? "var(--color-brand-light)"
              : licBand === "adequate" ? "var(--color-info-light)"
              : licBand ? "var(--color-warning-light)"
              : "transparent";
            return (
              <div key={lic.license_type_id} className="relative overflow-hidden rounded-xl bg-surface-secondary">
                {/* Left accent stripe — always visible, colored by readiness tier */}
                <div className="absolute inset-y-0 left-0 w-1 rounded-l-xl" style={{ backgroundColor: accentColor }} />
                {/* Progress fill — extends from left */}
                {lic.readiness_pct > 0 && (
                  <div
                    className="absolute inset-y-0 left-0"
                    style={{ width: `${lic.readiness_pct}%`, backgroundColor: fillBg }}
                  />
                )}
                {/* Content */}
                <div className="relative flex items-center gap-3 p-3 pl-4">
                  <span className="flex-shrink-0 text-[14px] font-semibold w-10 text-right" style={{ color: accentColor }}>
                    {lic.readiness_pct}%
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-medium truncate">{lic.license_name}</div>
                    <div className="text-[12px] text-text-tertiary">{lic.authority}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CONDITIONAL — a question for the developer. These hang on a fact we did not ask
            for (do you import equipment?). Listing them as "required" would demand a SONCAP
            certificate from someone sourcing locally; dropping them would hide one from
            someone importing. Both are wrong, so: shown, and honest about what decides it. */}
        {conditionalLics.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border">
            <h3 className="text-[14px] font-medium mb-1">Conditional — {conditionalLics.length} depend{conditionalLics.length === 1 ? "s" : ""} on one more answer</h3>
            <p className="text-[12px] text-text-tertiary mb-3">
              These apply only in certain cases. Tell us a bit more and we’ll rule them in or out.
            </p>
            <div className="space-y-2">
              {conditionalLics.map((lic) => (
                <div key={lic.license_type_id} className="rounded-xl bg-surface-secondary p-3 pl-4 border-l-4" style={{ borderLeftColor: "var(--color-info)" }}>
                  <div className="text-[14px] font-medium">{lic.license_name}</div>
                  <div className="text-[12px] text-text-tertiary">{lic.authority}</div>
                  {lic.applicability_reason && (
                    <div className="text-[12px] mt-1" style={{ color: "var(--color-info)" }}>{lic.applicability_reason}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* UNDER REVIEW — a gap on OUR side, not a question for them. The corpus records no
            basis for these yet. Saying so plainly beats the two dishonest alternatives:
            asserting them as required (which is how a village mini-grid got told it needed a
            transmission agreement), or hiding them. Shrinks as the regulatory corpus lands. */}
        {underReviewLics.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border">
            <h3 className="text-[14px] font-medium mb-1">Under review — {underReviewLics.length} we haven’t pinned down yet</h3>
            <p className="text-[12px] text-text-tertiary mb-3">
              We know these exist in {COUNTRY_META[state.country].name}, but we haven’t yet established
              exactly when they bind a project like yours — so we won’t claim they do. Our regulatory
              team is working through them.
            </p>
            <div className="space-y-1.5">
              {underReviewLics.map((lic) => (
                <div key={lic.license_type_id} className="flex items-baseline gap-2 text-[13px]">
                  <span className="text-text-tertiary">•</span>
                  <span className="flex-1">{lic.license_name}</span>
                  <span className="text-[11px] text-text-tertiary flex-shrink-0">{lic.authority}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOT day-one obligations. Real, and worth knowing about — but a developer planning a
            build should not have to read past them to find what they actually have to do. */}
        {/* "Only bites if you SEEK something" — a conditional, not an absolution. The earlier
            copy read "none you must / nobody is in breach for declining one", which invents a
            statement of law the corpus never made. The NERC Distribution Licence sits in this
            bucket today as an acknowledged interim placeholder, and it is mandatory for a
            mini-grid that distributes power. Say what is true: these apply if you go after
            them, and we have not established which projects they bind. */}
        <AsideList
          title={`If you pursue them — ${electiveLics.length}`}
          blurb="These bite only if you go after what they grant: a premium tariff, an exclusivity grant, a ruling. They are not part of getting built — but we have not yet established which projects they bind, so don't read this as “you're exempt”."
          rows={electiveLics}
        />
        <AsideList
          title={`Later — ${lifecycleLics.length} that come after`}
          blurb="Renewals and amendments. They act on a licence you don't hold yet, so they aren't part of getting built."
          rows={lifecycleLics}
        />
        <AsideList
          title={`Not yours to file — ${thirdPartyLics.length}`}
          blurb="These bind your contractor or your equipment, not you. Still worth verifying before you sign — just not a form you submit."
          rows={thirdPartyLics}
        />
      </section>

      {/* Compliance summary */}
      {result.compliance_summary && result.compliance_summary.total_obligations > 0 && (
        <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
          <h2 className="text-[17px] font-medium mb-3">Compliance Obligations</h2>
          <p className="text-[13px] text-text-tertiary mb-4">
            {COUNTRY_META[state.country].name} has {result.compliance_summary.total_obligations} regulatory compliance requirements for solar projects.
          </p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-danger-light rounded-xl p-2.5 text-center">
              <div className="text-[18px] font-medium text-danger">{result.compliance_summary.gate_tier_count}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Gate</div>
            </div>
            <div className="bg-warning-light rounded-xl p-2.5 text-center">
              <div className="text-[18px] font-medium text-warning-text">{result.compliance_summary.pre_operational_count}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Pre-operational</div>
            </div>
            <div className="bg-info-light rounded-xl p-2.5 text-center">
              <div className="text-[18px] font-medium text-info">{result.compliance_summary.ongoing_count}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Ongoing</div>
            </div>
          </div>
          {result.compliance_summary.key_gates.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-[14px] font-medium">Key Gate Requirements</h3>
              {result.compliance_summary.key_gates.map((gate) => (
                <div key={gate.display_name} className="p-2.5 bg-surface-secondary rounded-xl">
                  <div className="text-[14px] font-medium">{gate.display_name}</div>
                  <div className="text-[12px] text-text-tertiary">
                    {gate.approval_body}{gate.approval_timeline ? ` · ${gate.approval_timeline}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Email gate */}
      {!emailSubmitted ? (
        <section className="bg-ai-light border border-ai/20 rounded-2xl p-6 mb-4 print-hide">
          <div className="flex items-start gap-3">
            <Lock size={20} className="text-ai flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              {/* THIS PANEL PROMISED THINGS WE DO NOT DELIVER. Audited 2026-07-14:
                    - "the licenses your project needs"      → already shown, ABOVE this gate
                    - "the key questions to prepare for"     → that is "Questions Investors Will
                                                               Ask", which is behind
                                                               SHOW_BANKABILITY = false and never
                                                               renders for anyone
                    - "We'll send you a copy"                → there is NO email sender anywhere in
                                                               this codebase. Nothing is ever sent.
                  A developer handed over their email and got a document checklist plus two
                  promises we had no way to keep. That is a bluff at the exact moment we ask for
                  their trust, and "we don't bluff" cannot survive it.
                  The copy now promises only what is actually behind the gate. What SHOULD be
                  behind it is a product question for Peter — see the checkup notes. Do not
                  re-add a promise here without shipping the thing it promises. */}
              <h2 className="text-[17px] font-medium mb-1">See your document checklist</h2>
              <p className="text-[14px] text-text-secondary mb-4">
                Enter your email and we'll show you exactly which documents each of these licences
                needs — the ones you file, and the ones that support them.
                {!isEmbed && " You'll also be able to save or print the full report."}
              </p>
              <div className="flex gap-2">
                <input type="email" value={email} onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-ai/30" />
                <button onClick={onEmailSubmit} disabled={!isValidEmail(email)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-ai text-on-solid rounded-xl text-[14px] font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                  <Mail size={14} /> Unlock
                </button>
              </div>
              {/* Was: "We'll send you a copy. No spam, unsubscribe anytime." We cannot send a copy
                  — there is no mailer in this codebase. Print/Save-as-PDF is now part of the
                  gate's REWARD (it only renders once the email is in), so the promise moved into
                  the pitch above — and only in non-embed mode, because the embed never renders
                  the Print button and a promise the mode can't keep is the exact class of bluff
                  this copy was rewritten to remove. */}
              <p className="text-[12px] text-text-secondary mt-2">
                No spam. We use your email only to send/save your report and to follow up about Enereg.{" "}
                <a href="https://app.enereg.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-text-primary">Privacy Policy</a>.
              </p>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* THE REWARD FOR THE EMAIL. Three questions, straight from the corpus's own
              applicability conditions (lib/checkup-questions.ts), each resolving one or more of
              the "conditional" licences shown in the teaser. Answering "no" is as valuable as
              "yes" — it rules a requirement OUT. Every answer re-runs the evaluation in place, so
              the counts above visibly move as they go.
              Once ANY answer exists the panel stays mounted even at zero conditionals — an answer
              the developer can no longer see or change is a mis-click locked into a compliance
              answer, and hiding a licence on one wrong click is this product's worst failure. */}
          {((result.licenses_conditional ?? 0) > 0 || Object.keys(sharpeners).length > 0) && (
            <SharpenPanel sharpeners={sharpeners} onSharpen={onSharpen} refining={refining} sharpenError={sharpenError} />
          )}

          {/* Recommendations */}
          {result.nudges.length > 0 && (
            <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
              <h2 className="text-[17px] font-medium mb-3">Recommendations</h2>
              <div className="space-y-3">
                {result.nudges.slice(0, 5).map((nudge) => <NudgeCard key={nudge.id} nudge={nudge} />)}
              </div>
            </section>
          )}

          {/* Documents You'll Need */}
          {result.required_documents && result.required_documents.length > 0 && (() => {
            const submissions = result.required_documents.filter(d => d.is_submission);
            const supporting = result.required_documents.filter(d => !d.is_submission);
            return (
              <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
                <h2 className="text-[17px] font-medium mb-1">Documents You'll Need</h2>
                <p className="text-[13px] text-text-tertiary mb-4">
                  {/* Day-one rows only. The list used to span every licence shown, so it asked for
                      the paperwork behind an optional tariff uplift and a renewal the developer
                      cannot file — contradicting the roadmap directly above it. */}
                  These {result.required_documents.length} documents cover what you need to deal with now.
                </p>
                {submissions.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">Submission Documents</h3>
                    <p className="text-[12px] text-text-tertiary mb-2">Filed with your license applications</p>
                    <div className="space-y-1.5">
                      {submissions.map((doc) => (
                        <div key={doc.doc_type} className="flex items-start gap-2 p-2.5 bg-surface-secondary rounded-xl">
                          <FileText size={14} className="text-brand flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="text-[14px] font-medium">{doc.display_name}</div>
                            <div className="text-[12px] text-text-tertiary">Needed for: {doc.license_names.join(", ")}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {supporting.length > 0 && (
                  <div>
                    <h3 className="text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">Supporting Documents</h3>
                    <p className="text-[12px] text-text-tertiary mb-2">Support your applications but aren't formally submitted</p>
                    <div className="space-y-1.5">
                      {supporting.map((doc) => (
                        <div key={doc.doc_type} className="flex items-start gap-2 p-2.5 bg-surface-secondary rounded-xl">
                          <FileText size={14} className="text-text-tertiary flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="text-[14px] font-medium">{doc.display_name}</div>
                            <div className="text-[12px] text-text-tertiary">For: {doc.license_names.join(", ")}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })()}

          {/* Bankability — hidden by feature flag (#16) */}
          {SHOW_BANKABILITY && (
          <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
            <h2 className="text-[17px] font-medium mb-1">Bankability Assessment</h2>
            <p className="text-[13px] text-text-tertiary mb-4">How ready is your project for investor due diligence?</p>
            <div className="space-y-3">
              {result.bankability.dimensions.map((dim) => {
                const Icon = DIMENSION_ICONS[dim.dimension] || Shield;
                return (
                  <div key={dim.dimension} className="flex items-center gap-3">
                    <Icon size={16} className="text-text-tertiary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[14px] font-medium">{dim.dimension}</span>
                        <span className="text-[13px] text-text-secondary">{dim.score}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{
                          width: `${dim.score}%`,
                          backgroundColor: { strong: "var(--color-brand)", adequate: "var(--color-info)", marginal: "var(--color-warning-fill)", insufficient: "var(--color-danger)" }[band(dim.score, "bankability")],
                        }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {result.bankability.investor_questions.length > 0 && (
              <div className="mt-4 pt-3 border-t border-border">
                <h3 className="text-[14px] font-medium mb-2">Questions Investors Will Ask</h3>
                <ul className="space-y-1.5">
                  {result.bankability.investor_questions.map((q, i) => (
                    <li key={i} className="text-[13px] text-text-secondary flex gap-2">
                      <span className="text-text-tertiary">•</span>{q}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
          )}
        </>
      )}

      {/* CTA — this is not "sign up", it is "start the project you just checked". The link
          carries `from=checkup` + the token so the wizard can pre-fill once the user is
          admitted. Under the pilot gate (2026-07-15) admission happens on a booked
          onboarding call, so the copy promises the CALL, not an instant pre-filled project —
          the stash TTL is an hour and the checkup→call gap is days by design; a promise the
          wizard can't keep is the bluff this page's audit rule forbids. (The snapshot itself
          still resolves only by bearer token — never account email, the PR #63 leak.) */}
      <section className="bg-brand-light border border-brand/20 rounded-2xl p-6 text-center mb-4 print-hide">
        <h2 className="text-[17px] font-medium text-brand mb-2">Turn this into your project</h2>
        <p className="text-[14px] text-text-secondary mb-4">
          Enereg is in private pilot. Create your account, then book a short onboarding call
          — we&rsquo;ll set you up with an access code and your project workspace. It tracks
          all {result.fields_total} regulatory fields, pulls data from your documents, and
          generates ready-to-submit templates.
        </p>
        <a
          href={(() => {
            const params = checkupToken ? `?from=checkup&checkup_token=${encodeURIComponent(checkupToken)}` : "?from=checkup";
            return isEmbed ? `https://app.enereg.com/login${params}` : `/login${params}`;
          })()}
          target={isEmbed ? "_blank" : undefined}
          rel={isEmbed ? "noopener noreferrer" : undefined}
          onClick={() => postToParent?.("enereg-checkup-cta", { action: "create_project" })}
          className="inline-flex items-center gap-1.5 px-5 py-2 bg-brand text-on-solid rounded-xl text-[16px] font-medium hover:opacity-90 transition-opacity"
        >
          Create my project <ArrowRight size={16} />
        </a>
      </section>

      <div className="flex items-center justify-between print-hide">
        <button onClick={onStartOver} className="text-[14px] text-text-secondary hover:text-text-primary">Run another checkup</button>
        {/* Print/Save is part of the email's reward, not a free exit (Peter, 2026-07-14:
            "Print and save and download can come after they provide their emails"). The gate
            copy above promises it, so the two must move together. */}
        {!isEmbed && emailSubmitted && (
          <button onClick={() => window.print()} className="flex items-center gap-1.5 text-[14px] text-text-secondary hover:text-text-primary">
            <FileText size={14} /> Print / Save as PDF
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * A quiet list for rows that are real but are NOT day-one obligations on this developer —
 * optional incentives, later renewals, things that bind a contractor. Shown, because hiding a
 * requirement is the one unrecoverable mistake; set apart, because burying the four things you
 * must actually do under thirty things you might eventually consider is its own kind of lying.
 * Renders nothing when the corpus hasn't classified anything into it.
 */
function AsideList({ title, blurb, rows }: {
  title: string;
  blurb: string;
  rows: CheckupLicenseRow[];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="mt-5 pt-4 border-t border-border">
      <h3 className="text-[14px] font-medium mb-1">{title}</h3>
      <p className="text-[12px] text-text-tertiary mb-3">{blurb}</p>
      <div className="space-y-1.5">
        {rows.map((lic) => (
          <div key={lic.license_type_id} className="text-[13px]">
            <div className="flex items-baseline gap-2">
              <span className="text-text-tertiary">•</span>
              <span className="flex-1">
                {lic.license_name}
                {lic.subject && lic.subject !== "developer" && (
                  <span className="text-[11px] text-text-tertiary"> · your {lic.subject}</span>
                )}
                {lic.lifecycle_stage && (
                  <span className="text-[11px] text-text-tertiary"> · {lic.lifecycle_stage}</span>
                )}
                {/* Optional is an independent fact. A row filed under "not yours to file" can STILL
                    be voluntary, and without this the developer chases their contractor's optional
                    badge scheme believing it is a gate. */}
                {lic.elective && (
                  <span className="text-[11px] text-text-tertiary"> · optional</span>
                )}
              </span>
              <span className="text-[11px] text-text-tertiary flex-shrink-0">{lic.authority}</span>
            </div>
            {/* THE ROW'S OWN SAFETY FLAG. Filing a row into a drawer must never SUPPRESS the fact
                that we have not established who it binds — that is letting "elective" stand in
                for an answer, which is precisely what the corpus tells us not to do. Every one of
                the nine NG elective rows is undetermined today. */}
            {lic.undetermined && lic.applicability_reason && (
              <div className="ml-5 text-[11px]" style={{ color: "var(--color-warning-text)" }}>
                {lic.applicability_reason}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-surface border border-border rounded-xl p-3 text-center">
      <div className="text-[11px] text-text-tertiary uppercase tracking-wider">{label}</div>
      <div className="text-[11px] text-text-tertiary mb-1">{sub}</div>
      <div className="text-[18px] font-medium capitalize leading-tight">{value}</div>
    </div>
  );
}

function NudgeCard({ nudge }: { nudge: Nudge }) {
  const style = status("criticality", nudge.criticality);
  return (
    <div className="p-3 bg-surface-secondary rounded-xl">
      <div className="flex items-start gap-2 mb-1">
        <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${style.bg} ${style.text}`}>{style.label}</span>
        <span className="text-[11px] text-text-tertiary">~{nudge.effort}</span>
      </div>
      <h3 className="text-[14px] font-medium mb-1">{nudge.title}</h3>
      <p className="text-[13px] text-text-secondary">{nudge.body}</p>
      <p className="text-[12px] text-brand mt-1">{nudge.impact}</p>
    </div>
  );
}
